import {createApi,fetchBaseQuery} from "@reduxjs/toolkit/query/react"

export const ProductApi = createApi({
    reducerPath: "productApi",
    baseQuery: fetchBaseQuery({
        baseUrl:import.meta.env.VITE_API_URL,
        prepareHeaders:(headers)=>{
            const token = localStorage.getItem('_at') || null
            if(token){
                headers.set("Authorization", "Bearer "+token)
            }
        }
    }),
    tagTypes:['Product', 'ProductList', 'ProductDetail'],
    keepUnusedDataFor: 300,          // cache list data for 5 minutes after last subscriber
    refetchOnFocus: false,           // avoid surprise refetches while typing
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: 30,   // refetch if data is older than 30 seconds
    endpoints:(builder)=>({
        listAll: builder.query({
            query: ({ page = 1, limit = 10, search = '' }) => 
              `/product?page=${page}&limit=${limit}&search=${search}`,
            providesTags: (result, error, { page, limit, search }) => [
                'ProductList',
                // Tag each individual product for selective invalidation
                ...(result?.result?.map(product => ({ type: 'Product', id: product._id })) || []),
                // Tag the specific query for cache invalidation
                { type: 'ProductList', id: `${page}-${limit}-${search}` }
            ],
            // Transform response to optimize rendering
            transformResponse: (response) => ({
                ...response,                
                result: response.result?.map(product => ({
                    ...product,
                    // Pre-compute display values to avoid re-renders
                    displayPrice: `Rs.${product.price}/-`,
                    imageUrl: product.media?.[0]?.url
                })) || []
                
            }),
           
            // Enable background refetching for better UX
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.search !== previousArg?.search
            }
        }),
        createProduct:builder.mutation({
            query:(args)=> ({
                url: "/product",
                body:args,
                method:"POST",
                headers:()=>([
                    {"Content-Type":"multipart/form-data"}
                ])
            }),
            // Only invalidate product lists, not individual products
            invalidatesTags: ['ProductList'],
            // Optimistically update cache
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    // Update existing queries with new product
                    dispatch(
                        ProductApi.util.updateQueryData('listAll', { page: 1, limit: 10, search: '' }, (draft) => {
                            if (draft.result) {
                                draft.result.unshift({
                                    ...data.result,
                                    displayPrice: `Rs.${data.result.price}/-`,
                                    imageUrl: data.result.mainImage || data.result.images?.[0]?.url
                                })
                                // Keep only the latest items to avoid memory bloat
                                if (draft.result.length > 10) {
                                    draft.result = draft.result.slice(0, 10)
                                }
                            }
                        })
                    )
                } catch {}
            }
        }),
        listForHome:builder.query({
            query:({search = '' }) =>`/product/list?search=${encodeURIComponent(search)}`,
            providesTags: (result, error, { search }) => [
                'ProductList',
                // Tag with search parameter to differentiate cached queries
                { type: 'ProductList', id: `home-${search || 'all'}` }
            ],
            keepUnusedDataFor: 600, // Cache home products for 10 minutes
            transformResponse: (response) => ({
                ...response,
                result: response.result?.data?.map(product => ({
                    ...product,
                    imageUrl: product.media?.[0]?.url
                })) || []
            }),
            // Force refetch when search parameter changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.search !== previousArg?.search;
            }
        }),
        listById:builder.query({
            query:(id)=>`/product/${id}`,
            providesTags: (result, error, id) => [
                { type: 'Product', id }
            ],
            // Keep individual product data for 15 minutes
            keepUnusedDataFor: 900, // Cache individual products for 10 minutes
        }),
        deleteProduct:builder.mutation({
            query:(id)=>({
                url:`/product/${id}`,
                method:"DELETE"
            }),
            // Selectively invalidate only the deleted product and lists
            invalidatesTags: (result, error, id) => [
                { type: 'Product', id },
                'ProductList'
            ],
            // Optimistically remove from cache
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                // Optimistically remove from all list queries
                const patchResults = []
                
                // Update all existing list queries
                dispatch(ProductApi.util.invalidateTags(['ProductList']))
                
                try {
                    await queryFulfilled
                } catch {
                    // Revert optimistic updates on failure
                    patchResults.forEach(patchResult => {
                        patchResult.undo()
                    })
                }
            }
        }),
        editProduct:builder.mutation({
            query:({id,payload})=>({
                url: `/product/${id}`,
                method:'PUT',
                body:payload,
                headers:()=>([
                    {"Content-Type" :"multipart/form-data"}
                ])
            }),
            // Only invalidate the specific product and lists
            invalidatesTags: (result, error, { id }) => [
                { type: 'Product', id },
                'ProductList'
            ],
            // Optimistically update cache
            async onQueryStarted({ id, payload }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    
                    // Update the specific product in cache
                    dispatch(
                        ProductApi.util.updateQueryData('listById', id, (draft) => {
                            Object.assign(draft, data.result)
                        })
                    )
                    
                    // Update product in list queries
                    const updatedProduct = {
                        ...data.result,
                        displayPrice: `Rs.${data.result.price}/-`,
                        imageUrl: data.result.mainImage || data.result.images?.[0]?.url
                    }
                    
                    // Update all list queries that might contain this product
                    dispatch(ProductApi.util.invalidateTags(['ProductList']))
                    
                } catch {}
            }
        }),
    })

}) 

export const {
    useListAllQuery, 
    useListForHomeQuery,
    useCreateProductMutation, 
    useListByIdQuery, 
    useDeleteProductMutation, 
    useEditProductMutation,
    // Export utilities for manual cache management
    util: { 
        invalidateTags: invalidateProductTags, 
        updateQueryData: updateProductQueryData 
    }
} = ProductApi